export type AspectRatioPreset =
  | 'fluid'
  | '1:1'
  | '4:5'
  | '9:16'
  | '16:9'
  | '4:3'
  | '3:4'
  | '3:2'
  | '2:3'
  | 'a4'
  | 'a5'
  | 'letter';

export type Orientation = 'landscape' | 'portrait';

export interface BlockFormatConfig {
  aspectRatio: AspectRatioPreset;
  orientation: Orientation;
}

export const DEFAULT_BLOCK_FORMAT: BlockFormatConfig = {
  aspectRatio: 'fluid',
  orientation: 'portrait',
};

export interface AspectRatioMeta {
  value: AspectRatioPreset;
  label: string;
  description: string;
  ratio: number | null;
  canFlip: boolean;
}

export const ASPECT_RATIO_META: Record<AspectRatioPreset, AspectRatioMeta> = {
  fluid: {
    value: 'fluid',
    label: 'Fluid',
    description: 'Adapts to content',
    ratio: null,
    canFlip: false,
  },
  '1:1': {
    value: '1:1',
    label: '1:1',
    description: 'Square',
    ratio: 1,
    canFlip: false,
  },
  '4:5': {
    value: '4:5',
    label: '4:5',
    description: 'Portrait',
    ratio: 4 / 5,
    canFlip: true,
  },
  '9:16': {
    value: '9:16',
    label: '9:16',
    description: 'Stories',
    ratio: 9 / 16,
    canFlip: true,
  },
  '16:9': {
    value: '16:9',
    label: '16:9',
    description: 'Widescreen',
    ratio: 16 / 9,
    canFlip: true,
  },
  '4:3': {
    value: '4:3',
    label: '4:3',
    description: 'Classic',
    ratio: 4 / 3,
    canFlip: true,
  },
  '3:4': {
    value: '3:4',
    label: '3:4',
    description: 'Portrait classic',
    ratio: 3 / 4,
    canFlip: true,
  },
  '3:2': {
    value: '3:2',
    label: '3:2',
    description: 'Photo',
    ratio: 3 / 2,
    canFlip: true,
  },
  '2:3': {
    value: '2:3',
    label: '2:3',
    description: 'Portrait photo',
    ratio: 2 / 3,
    canFlip: true,
  },
  a4: {
    value: 'a4',
    label: 'A4',
    description: 'DIN A4',
    ratio: 210 / 297,
    canFlip: true,
  },
  a5: {
    value: 'a5',
    label: 'A5',
    description: 'DIN A5',
    ratio: 148 / 210,
    canFlip: true,
  },
  letter: {
    value: 'letter',
    label: 'Letter',
    description: 'US Letter',
    ratio: 8.5 / 11,
    canFlip: true,
  },
};

export interface BlockFormatOption {
  id: string;
  label: string;
  aspectRatio: AspectRatioPreset;
  orientation: Orientation;
}

export const BLOCK_FORMAT_PRESETS: BlockFormatOption[] = [
  { id: 'fluid', label: 'Fluid', aspectRatio: 'fluid', orientation: 'portrait' },
  { id: 'square', label: '1:1', aspectRatio: '1:1', orientation: 'portrait' },
  { id: 'portrait', label: '4:5', aspectRatio: '4:5', orientation: 'portrait' },
  { id: 'stories', label: '9:16', aspectRatio: '9:16', orientation: 'portrait' },
  { id: 'wide', label: '16:9', aspectRatio: '16:9', orientation: 'landscape' },
  { id: 'classic', label: '4:3', aspectRatio: '4:3', orientation: 'landscape' },
  { id: 'photo', label: '3:2', aspectRatio: '3:2', orientation: 'landscape' },
  { id: 'a5', label: 'A5', aspectRatio: 'a5', orientation: 'portrait' },
  { id: 'a4', label: 'A4', aspectRatio: 'a4', orientation: 'portrait' },
  { id: 'letter', label: 'Letter', aspectRatio: 'letter', orientation: 'portrait' },
];

const BLOCK_BASE_WIDTH = 400;
const BLOCK_MAX_WIDTH = 560;
const BLOCK_MIN_HEIGHT = 200;
const BLOCK_DOC_WIDTH = 520;

export function getEffectiveRatio(config: BlockFormatConfig): number | null {
  const meta = ASPECT_RATIO_META[config.aspectRatio];
  if (!meta.ratio) return null;

  let ratio = meta.ratio;
  const isLandscape = ratio > 1;
  const wantsLandscape = config.orientation === 'landscape';

  if (meta.canFlip && isLandscape !== wantsLandscape) {
    ratio = 1 / ratio;
  }

  return ratio;
}

export function calculateBlockDimensions(config: BlockFormatConfig): { width: number; height: number | 'auto' } {
  const meta = ASPECT_RATIO_META[config.aspectRatio];
  if (config.aspectRatio === 'fluid' || !meta.ratio) {
    return { width: BLOCK_BASE_WIDTH, height: 'auto' };
  }

  if (config.aspectRatio === 'a4' || config.aspectRatio === 'a5' || config.aspectRatio === 'letter') {
    const ratio = getEffectiveRatio(config) || meta.ratio;
    const height = Math.round(BLOCK_DOC_WIDTH / ratio);
    return { width: BLOCK_DOC_WIDTH, height };
  }

  const ratio = getEffectiveRatio(config) || meta.ratio;

  if (ratio > 1) {
    const height = Math.round(BLOCK_MAX_WIDTH / ratio);
    return { width: BLOCK_MAX_WIDTH, height };
  }

  const height = Math.round(BLOCK_BASE_WIDTH / ratio);
  return { width: BLOCK_BASE_WIDTH, height };
}
